import moment from 'moment'
import { v4 } from 'uuid'

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import localizationIcon from '@assets/images/observation-localization-icon.svg'

import { useScrollTo } from '@utils/hooks/useScrollTo'
import { useWindowSize } from '@utils/hooks'

import { DatePicker } from '@components/DatePicker'
import { DropDown } from '@components/DropDown'
import { Input } from '@components/Input'
import { UploadPicture } from '@components/UploadPicture'

import { SelectCompany } from '@modules/_shared/components/SelectCompany'
import { SelectPhase } from '@modules/_shared/components/SelectPhase'
import { SelectTrade } from '@modules/_shared/components/SelectTrade'
import { PdfMarkerPreview } from '@modules/_shared/components/PdfMarkerPreview'
import { SelectObservations } from '@modules/_shared/components/SelectObservations'
import { LocalizationEdit } from '@modules/_shared/components/LocalizationEdit'

import { Company, LocalFile, Observation, Phase, ProjectFile, ProjectFilePosition, RemoteFile, Trade } from '@types'

import {
  DatePickerContainer,
  DatePickerInput,
  FormContainer,
  LocalizationFormContainer,
  TextButtonLocalization,
  TitleLocalization,
  ViewerContainer,
} from './ObservationEdit.styled'

export interface FormData {
  name: string
  trade?: Trade
  company?: Company
  phase?: Phase
  limitDate?: Date
  projectFile?: ProjectFile
  projectFilePosition?: ProjectFilePosition
}

type Props = {
  formData: FormData
  projectId: string
  observation?: Observation
  files: (LocalFile | RemoteFile)[]
  planFile?: RemoteFile
  disableSearches?: boolean
  disabledAddCompany?: boolean
  hideProjectFilePosition?: boolean
  hidePictures?: boolean
  mode: 'form' | 'localization'
  onFormChange: (formData: FormData) => void
  onFilesChange: (files: (LocalFile | RemoteFile)[]) => void
  onAddLocalization: () => void
}

export const ObservationEdit: React.FC<Props> = ({
  formData,
  projectId,
  disableSearches = false,
  disabledAddCompany = false,
  hideProjectFilePosition = false,
  hidePictures = false,
  files,
  planFile,
  observation,
  mode = 'form',
  onFormChange,
  onFilesChange,
  onAddLocalization,
}: Props) => {
  const { t } = useTranslation()
  const scrollTo = useScrollTo()
  const { isMobile } = useWindowSize()

  useEffect(() => {
    scrollTo('container')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  return (
    <>
      {mode === 'form' ? (
        <FormContainer mode={mode} id="container">
          <>
            {isMobile ? (
              <Input
                label={t('modules_project-observations_components_ObservationEdit_name_label')}
                placeholder={t('modules_project-observations_components_ObservationEdit_name_placeholder')}
                type="text"
                required
                maxLength={255}
                value={formData.name}
                onChange={(value) => {
                  onFormChange({ ...formData, name: value })
                }}
              />
            ) : (
              <SelectObservations
                projectId={projectId}
                autoFocus
                required
                label={t('modules_project-observations_components_ObservationEdit_name_label')}
                placeholder={t('modules_project-observations_components_ObservationEdit_name_placeholder')}
                observationName={formData.name}
                onClick={(observation) => {
                  onFormChange({
                    ...formData,
                    name: observation.name,
                    trade: observation.trade,
                    phase: observation.phase,
                    company: observation.company,
                  })
                }}
                onSearch={(name) => onFormChange({ ...formData, name: name })}
              />
            )}

            <SelectTrade
              required
              disabledSearch={disableSearches}
              trade={formData.trade}
              onSelectTrade={(trade) => {
                onFormChange({ ...formData, trade })
              }}
            />

            <SelectCompany
              disabledAddCompany={disabledAddCompany}
              disabledSearch={disableSearches}
              projectId={projectId || ''}
              company={formData.company}
              onSelectCompany={(company) => {
                onFormChange({ ...formData, company })
              }}
            />

            <SelectPhase
              disabledSearch={disableSearches}
              projectId={projectId || ''}
              phase={formData.phase}
              onSelectPhase={(phase) => {
                onFormChange({ ...formData, phase })
              }}
            />

            <DropDown
              WrappedComponent={({ show }) => (
                <DatePickerInput onClick={show}>
                  <Input
                    type="text"
                    label={t('modules_project-observations_components_ObservationEdit_limit_date_label')}
                    placeholder={t('modules_project-observations_components_ObservationEdit_limit_date_placeholder')}
                    value={formData.limitDate ? moment(formData.limitDate).format('DD/MM/YYYY') : ''}
                  />
                  <DatePickerContainer />
                </DatePickerInput>
              )}
              WrappedChildren={({ hide }) => (
                <DatePicker
                  minDate={moment().add('1', 'day').toDate()}
                  selectedDate={formData.limitDate}
                  onChange={(date) => {
                    if (date) {
                      onFormChange({ ...formData, limitDate: date })
                      hide()
                    }
                  }}
                />
              )}
            />

            {!hideProjectFilePosition && (
              <LocalizationFormContainer>
                <TitleLocalization>
                  {t('modules_project-observations_components_ObservationEdit_localization_label')}
                </TitleLocalization>

                {!formData.projectFilePosition ? (
                  <TextButtonLocalization type="button" onClick={onAddLocalization}>
                    <img src={localizationIcon} />
                    {t('modules_project-observations_components_ObservationEdit_localization_button_label')}
                  </TextButtonLocalization>
                ) : (
                  <ViewerContainer onClick={onAddLocalization}>
                    {planFile?.url && formData?.projectFile && (
                      <PdfMarkerPreview
                        pdfUrl={planFile.url}
                        initialMarker={{
                          id: v4(),
                          pageNumber: 1,
                          type: 'observation',
                          x: formData.projectFilePosition.x,
                          y: formData.projectFilePosition.y,
                          data: { ...observation, trade: formData.trade },
                        }}
                      />
                    )}
                  </ViewerContainer>
                )}
              </LocalizationFormContainer>
            )}

            {!hidePictures && (
              <UploadPicture
                multiple
                label={t('modules_project-observations_components_ObservationEdit_photos_label')}
                files={files}
                setFiles={onFilesChange}
              />
            )}
          </>
        </FormContainer>
      ) : (
        <LocalizationEdit
          id="container"
          markerType="observation"
          file={planFile}
          projectFile={formData.projectFile}
          projectId={projectId}
          data={{
            ...observation,
            name: formData.name,
            trade: formData.trade,
            projectFile: formData.projectFile,
            projectFilePositions: formData.projectFilePosition
              ? [
                  {
                    x: formData.projectFilePosition.x,
                    y: formData.projectFilePosition.y,
                  },
                ]
              : undefined,
          }}
          onAdd={(projectFilePosition: ProjectFilePosition) => onFormChange({ ...formData, projectFilePosition })}
          onChange={(projectFilePosition: ProjectFilePosition) => onFormChange({ ...formData, projectFilePosition })}
          onRemove={() => onFormChange({ ...formData, projectFilePosition: undefined })}
          onChangeProjectFile={(_, projectFile) => {
            onFormChange({
              ...formData,
              projectFile,
              projectFilePosition: projectFile ? formData.projectFilePosition : undefined,
            })
          }}
        />
      )}
    </>
  )
}
